import SendIcon from '@mui/icons-material/Send'
import { Box, ButtonBase, TextField } from '@mui/material'
import React, { KeyboardEvent, useRef, useState } from 'react'

interface ChatInputProps {
    onSend: (text: string) => void
    disabled?: boolean
}

export const ChatInput = ({ onSend, disabled }: ChatInputProps) => {
    const [value, setValue] = useState('')
    const inputRef = useRef<HTMLInputElement>(null)

    const handleSend = () => {
        const text = value.trim()
        if (!text || disabled) return
        onSend(text)
        setValue('')
        inputRef.current?.focus()
    }

    const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault()
            handleSend()
        }
    }

    return (
        <Box
            sx={{
                padding: '12px 14px',
                borderTop: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'flex-end',
                gap: '8px',
                backgroundColor: '#fff',
                flexShrink: 0,
            }}
            data-test="chat-input-area"
        >
            <TextField
                inputRef={inputRef}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask about experiments..."
                multiline
                maxRows={3}
                size="small"
                fullWidth
                disabled={disabled}
                sx={{
                    '& .MuiOutlinedInput-root': {
                        borderRadius: '10px',
                        fontSize: '13.5px',
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'primary.main',
                        },
                    },
                }}
                data-test="chat-input"
            />
            <ButtonBase
                onClick={handleSend}
                disabled={!value.trim() || disabled}
                sx={{
                    width: 40,
                    height: 40,
                    minWidth: 40,
                    borderRadius: '10px',
                    backgroundColor: '#08294e',
                    color: '#fff',
                    flexShrink: 0,
                    display: 'grid',
                    placeItems: 'center',
                    opacity: !value.trim() || disabled ? 0.4 : 1,
                    '&:hover': {
                        backgroundColor: '#395371',
                        transform: 'scale(1.05)',
                    },
                    transition: 'all 0.15s',
                }}
                data-test="chat-send"
            >
                <SendIcon sx={{ fontSize: 20 }} />
            </ButtonBase>
        </Box>
    )
}
